import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/shared/ui/tooltip';
import {Calendar, Clock, Info} from 'lucide-react';
import {Badge} from '@/shared/ui/badge';
import {Input} from '@/shared/ui/input';
import {Label} from '@/shared/ui/label';
import {Switch} from '@/shared/ui/switch';
import {ToggleGroup, ToggleGroupItem} from '@/shared/ui/toggle-group';
import {
  WINDOW_LIMIT_HOURS,
  WINDOW_LIMIT_SESSIONS,
  formatWindowCapPreview,
  type WindowCapFormState,
  type WindowLimitBasis,
} from '../model/participantWindowCap';

const WINDOW_CAP_HINT =
  'Restrict the total number of hours or sessions a participant can complete within a rolling time window.';

const WINDOW_CAP_PREVIEW_PLACEHOLDER =
  'Enter a limit and time window to see how this rule will apply.';

const InfoHint = ({text}: {text: string}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const ParticipantWindowCapRule = ({
  idPrefix,
  value,
  onChange,
  isEditing,
}: {
  idPrefix: string;
  value: WindowCapFormState;
  onChange: (next: WindowCapFormState) => void;
  isEditing: boolean;
}) => {
  const preview = formatWindowCapPreview(value);
  const capacityLabel =
    value.basis === WINDOW_LIMIT_SESSIONS
      ? 'Max Sessions Limit per window'
      : 'Max Hours Limit per window';
  const capacityHint =
    value.basis === WINDOW_LIMIT_SESSIONS ? 'sessions' : 'hrs';
  const capacityStep = value.basis === WINDOW_LIMIT_SESSIONS ? '1' : '0.1';

  const setField = (patch: Partial<WindowCapFormState>) =>
    onChange({...value, ...patch});

  return (
    <div className="space-y-3 py-3 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <Switch
          id={`${idPrefix}-window-cap`}
          checked={value.enabled}
          disabled={!isEditing}
          onCheckedChange={(checked) => setField({enabled: checked === true})}
          className="mt-0.5"
        />
        <div>
          <Label
            htmlFor={`${idPrefix}-window-cap`}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground"
          >
            Cap participant sessions during a time window
            <Badge variant="secondary">NEW</Badge>
            <InfoHint text={WINDOW_CAP_HINT} />
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">
            Restrict the total number of hours or sessions a participant can complete
            within a rolling time window.
          </p>
        </div>
      </div>

      {value.enabled && (
        <div className="ml-0 sm:ml-11 rounded-lg border border-border p-4 space-y-4">
          <Label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Configure Limit
          </Label>

          <div className="space-y-2">
            <Label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Limit Basis
            </Label>
            <ToggleGroup
              type="single"
              value={value.basis}
              onValueChange={(next) => {
                if (!isEditing || !next) return;
                setField({basis: next as WindowLimitBasis, capacity: ''});
              }}
              className="flex flex-wrap justify-start gap-2"
              disabled={!isEditing}
            >
              <ToggleGroupItem
                value={WINDOW_LIMIT_HOURS}
                aria-label="By Hours"
                disabled={!isEditing}
                className="h-9 px-4 rounded-md border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Clock className="mr-1.5 h-3.5 w-3.5" />
                By Hours
              </ToggleGroupItem>
              <ToggleGroupItem
                value={WINDOW_LIMIT_SESSIONS}
                aria-label="By Sessions"
                disabled={!isEditing}
                className="h-9 px-4 rounded-md border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Calendar className="mr-1.5 h-3.5 w-3.5" />
                By Sessions
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                {capacityLabel} <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min={value.basis === WINDOW_LIMIT_SESSIONS ? '1' : '0.1'}
                step={capacityStep}
                value={value.capacity}
                disabled={!isEditing}
                onChange={(e) => setField({capacity: e.target.value})}
                className={isEditing ? 'bg-white' : 'bg-gray-50 text-gray-700'}
              />
              <p className="text-xs text-muted-foreground">
                {value.basis === WINDOW_LIMIT_SESSIONS
                  ? capacityHint
                  : 'Total hours across the full time window'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Time Window Duration (days) <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={value.windowDays}
                disabled={!isEditing}
                onChange={(e) => setField({windowDays: e.target.value})}
                className={isEditing ? 'bg-white' : 'bg-gray-50 text-gray-700'}
              />
              <p className="text-xs text-muted-foreground">
                Rolling window length in days (e.g. 7 = weekly)
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-normal text-foreground">
                Max Hours Per Day
              </Label>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={value.maxHoursPerDay}
                disabled={!isEditing}
                onChange={(e) => setField({maxHoursPerDay: e.target.value})}
                className={isEditing ? 'bg-white' : 'bg-gray-50 text-gray-700'}
              />
              <p className="text-xs text-muted-foreground">
                Caps total hours worked per participant in a single calendar day
              </p>
            </div>
          </div>

          <div className="flex gap-2 rounded-lg border border-border bg-muted/50 p-3">
            <Info className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Active Configuration Preview</p>
              <p className="text-sm text-muted-foreground mt-1">
                {preview ?? WINDOW_CAP_PREVIEW_PLACEHOLDER}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
